import { humanise, stateWords } from '@/ui/words';
import { prospectingOf } from '@norum/contracts';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { CanonicalPanel,ContactIdentity,useCanonicalRedirect } from './Canonical';
import { CompanyWorkspace } from './Workspace';
import { failureOf, said } from '@/api/errors';
import { useAssignContact, useCompany, useContact, useDriveFiles, useDriveFolders, useIncoming, useWriteCompanyRecord, useManualCommunications, useLogManualCommunication, useCommunicationCapabilities, useCreateCommunicationTask, useUploadManualCommunicationAttachment, useDownloadManualCommunicationAttachment, type WireCompanyRelated } from '@/api/hooks';
import { useToast } from '@/ui/Toast';
import { useMeOrThrow } from '@/app/MeContext';
import type { IncomingResponse, WireEmployment, WireFolder, WireProspect } from '@/api/wire';
import { Button, Field, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { Dialog } from '@/ui/Dialog';
import { Select, Tab, Textarea } from '@/ui/fields';
import { FactList, RecordHeader, RelatedList, Timeline, statusChip, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { relationshipChip } from '@/ui/people';
import { ApproveCounterparty, EditCompany, EditContact, MoveContact } from './flows';
import { RequestErasure } from '@/people/Erasure';
import { AgentBrief } from '@/ui/brief';
import { useTechnical } from '@/app/technical';
import { useDepartmentLabel } from '@/app/departments';
import { actorLabel } from '@/today/copy';
import { useFrameMaybe } from '@/app/frame';
import { Explainer } from '@/ui/context';
import { useWho, type Who } from '@/ui/who';
import {
  explainCompany,
  explainContact,
  explainEmployment,
  explainFolder,
  explainLink,
  explainLinkedThread,
  explainRelatedBuyer,
  explainRelatedProspect,
  explainRelatedSignal,
  explainRelatedSupplier,
} from './explain';
import { viewerOf, type Viewer } from './explainKit';

/**
 * A contact and a company, as records (DECISIONS, "Record"): who they are,
 * what we are to each other, where they have worked — each employment its
 * own link with a start and an end — and every conversation around them.
 */
export function ContactPage() {
  const { id = null } = useParams();
  useCanonicalRedirect('contact', id);
  const contact = useContact(id);
  const incoming = useIncoming(id ? { kind: 'contact', id } : null);
  const [flow, setFlow] = useState<'edit' | 'move' | 'approve' | 'erase' | null>(null);
  const who = useWho();
  const me = useMeOrThrow();

  if (contact.isPending) return <Skeleton rows={8} />;
  if (contact.isError) return <div className="p-6"><Refusal failure={failureOf(contact.error)} /></div>;
  const c = contact.data.contact;
  const viewer = viewerOf(me);

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker="Contact"
        title={c.fullName}
        subtitle={c.currentCompanyName ? `${c.currentRole ?? 'Works'} · ${c.currentCompanyName}` : 'No current employment'}
        meta={[c.email, c.phone].filter(Boolean).join(' · ') || null}
        chips={[relationshipChip(c.relationship)]}
        actions={
          <>
            {c.relationship === 'unapproved' && (
              <Button icon="check" size="row" variant="primary" onClick={() => setFlow('approve')}>Approve them</Button>
            )}
            {c.relationship !== 'unapproved' && (
              <Button size="row" onClick={() => setFlow('approve')}>Change the relationship</Button>
            )}
            <Button size="row" onClick={() => setFlow('move')}>Move company</Button>
            <Button icon="edit" size="row" onClick={() => setFlow('edit')}>Edit</Button>
            <Button size="row" variant="quiet" onClick={() => setFlow('erase')}>They asked to be forgotten</Button>
          </>
        }
      />
      <Explainer startOpen label="About this contact" context={explainContact(c, who, viewer, { onPage: true })} />
      <FactList
        facts={[
          ['Owner', c.ownerName],
          ['Last heard from', c.lastInboundAt ? when(c.lastInboundAt) : 'Not yet'],
          ['Last written to', c.lastOutboundAt ? when(c.lastOutboundAt) : 'Not yet'],
          ['Relationship', c.relationship === 'unapproved' ? 'Unapproved — nobody has said we do business with them' : `${c.relationship}, approved by ${c.relationshipApprovedByName ?? 'a person'} on ${when(c.relationshipApprovedAt)}`],
          ['In the database since', when(c.createdAt)],
        ]}
      />
      <ContactIdentity id={c.id}/>
      <HandOver contactId={c.id} ownerHuman={c.ownerHuman} />
      <Timeline
        title="Where they have worked"
        entries={c.employments.map(employmentEntry('company', who, viewer))}
        note="Each employment is its own link with a start and an end. When someone moves, one closes and another opens — both companies keep their own clean history."
      />
      <Incoming incoming={incoming.data} pending={incoming.isPending} />
      {/* Identifiers and fixed words only: an agent resolves the rest by id (SG-06, §11). */}
      <AgentBrief
        lines={[
          ['contact', c.id],
          ['company', c.currentCompanyId],
          ['relationship', c.relationship],
          ['owner', c.ownerHuman],
          ['employments', c.employments?.length || null],
        ]}
      />
      {flow === 'edit' && <EditContact contact={c} onClose={() => setFlow(null)} />}
      {flow === 'move' && <MoveContact contact={c} onClose={() => setFlow(null)} />}
      {flow === 'erase' && <RequestErasure contactId={c.id} name={c.fullName} onClose={() => setFlow(null)} />}
      {flow === 'approve' && (
        <ApproveCounterparty kind="contact" id={c.id} name={c.fullName} current={c.relationship} onClose={() => setFlow(null)} />
      )}
    </div>
  );
}

const COMPANY_TABS = [
  ['identity', 'Identity and history'],
  ['overview', 'Overview'],
  ['records', 'All work'],
  ['communications', 'Communication log'],
  ['people', 'People'],
  ['conversations', 'Conversations'],
  ['work', 'Prospecting and deals'],
  ['files', 'Files'],
] as const;
type CompanyTab = (typeof COMPANY_TABS)[number][0];

/**
 * A company, in one place (the owner: "all related contacts, all related
 * communication, all related research … one central use for us"): its
 * details for everyone (0128), its people, what was said, what this
 * department holds about it, and its papers — each on its own tab.
 */
export function CompanyPage() {
  const { id = null } = useParams();
  useCanonicalRedirect('company', id);
  const company = useCompany(id);
  const incoming = useIncoming(id ? { kind: 'company', id } : null);
  const folders = useDriveFolders(id ?? undefined);
  const [flow, setFlow] = useState<'edit' | 'approve' | 'details' | 'communication' | null>(null);
  const [params, setParams] = useSearchParams();
  const pickedTab = params.get('tab');
  const tab: CompanyTab = COMPANY_TABS.some(([key]) => key === pickedTab) ? pickedTab as CompanyTab : 'overview';
  const setTab = (next: CompanyTab) => { const p = new URLSearchParams(params); p.set('tab', next); p.delete('offset'); setParams(p, { replace: true }); };
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const who = useWho();
  // Find companies like this (0130): a lead of a department that prospects
  // starts a plan from it; more of our vendors are suppliers, more of
  // ourselves competitors.
  const findsLikeIt = prospectingOf(me.department) !== null && (me.isSuperadmin || me.leads.includes(me.department));

  if (company.isPending) return <Skeleton rows={8} />;
  if (company.isError) return <div className="p-6"><Refusal failure={failureOf(company.error)} /></div>;
  const c = company.data.company;
  const details = company.data.details ?? null;
  const related = company.data.related;
  const viewer = viewerOf(me);
  const counts: Partial<Record<CompanyTab, number>> = {
    people: c.employments.length,
    conversations: incoming.data?.threads.length ?? 0,
    work: related ? related.prospects.length + related.buyers.length + related.suppliers.length : 0,
  };

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker="Company"
        title={c.displayName ?? c.legalName}
        subtitle={c.displayName ? c.legalName : null}
        meta={[c.domain, `${c.peopleNow} ${c.peopleNow === 1 ? 'person' : 'people'} here now`].filter(Boolean).join(' · ')}
        chips={[relationshipChip(c.relationship)]}
        actions={
          <>
            {c.relationship === 'unapproved' ? (
              <Button icon="check" size="row" variant="primary" onClick={() => setFlow('approve')}>Approve as a counterparty</Button>
            ) : (
              <Button size="row" onClick={() => setFlow('approve')}>Change the relationship</Button>
            )}
            <Button size="row" onClick={() => setFlow('details')}>Write details down</Button>
            <Button size="row" onClick={() => setFlow('communication')}>Log communication</Button>
            <Button icon="edit" size="row" onClick={() => setFlow('edit')}>Edit</Button>
            {findsLikeIt && id && (
              <Button size="row" onClick={() => void navigate(`/prospecting/setup?like=${id}`)}>
                {c.relationship === 'internal' ? 'Find our competitors' : c.relationship === 'vendor' ? 'Find suppliers like this' : 'Find companies like this'}
              </Button>
            )}
          </>
        }
      />
      <Explainer
        startOpen
        label="About this company"
        context={explainCompany(c, who, viewer, { onPage: true, details: details !== null, prospects: related ? related.prospects.length : null })}
      />
      <div role="tablist" aria-label="About this company" className="flex flex-wrap border-b border-line">
        {COMPANY_TABS.map(([key, label]) => (
          <Tab key={key} selected={tab === key} onClick={() => setTab(key)}>
            {label}
            {counts[key] ? ` · ${counts[key]}` : ''}
          </Tab>
        ))}
      </div>

      {tab === 'identity' && <CanonicalPanel kind="company" id={c.id} />}
      {tab === 'records' && <CompanyWorkspace companyId={c.id} />}
      {tab === 'communications' && <><CommunicationCapabilities /><ManualCommunications companyId={c.id} /></>}
      {tab === 'overview' && (
        <>
          <CompanyWorkspace companyId={c.id} preview />
          <section aria-label="People preview"><Kicker>People · {c.employments.length}</Kicker><Timeline title="Latest contacts" entries={c.employments.slice(0,3).map(employmentEntry('contact',who,viewer))} /></section>
          <Incoming companyId={c.id} incoming={incoming.data ? { ...incoming.data, threads: incoming.data.threads.slice(0,3) } : undefined} pending={incoming.isPending} />
          <FactList
            facts={[
              ['Relationship', c.relationship === 'unapproved' ? 'Unapproved — approving a company approves everyone employed there today' : `${c.relationship}, approved by ${c.relationshipApprovedByName ?? 'a person'} on ${when(c.relationshipApprovedAt)}`],
              ['Domain', c.domain],
              ['On record since', when(c.createdAt)],
            ]}
          />
          {/* Its details on the company itself, for every department (0128):
              the fixed core keeps a name and a domain and no more. */}
          <section className="flex flex-col gap-2" aria-label="Company details" data-testid="company-written">
            <Kicker>Company details</Kicker>
            {details ? (
              <FactList
                facts={[
                  ['Address', details.postalAddress],
                  ['Phone', details.phone],
                  ['Website', details.website],
                  ['Org number', details.orgNumber],
                  ['Note', details.note],
                  ['Where from', detailsSource(details)],
                ]}
              />
            ) : (
              <p className="m-0 text-dense text-secondary">Nothing written down yet. Open its website or its listing and write down what you read.</p>
            )}
            <Button size="row" className="self-start" onClick={() => setFlow('details')}>{details ? 'Correct the details' : 'Write details down'}</Button>
          </section>
        </>
      )}

      {tab === 'people' && (
        <Timeline
          title="People, now and before"
          entries={c.employments.map(employmentEntry('contact', who, viewer))}
          note="The same employment links read from the company's side. Someone leaving does not erase what they did here."
        />
      )}

      {tab === 'conversations' && <Incoming companyId={c.id} incoming={incoming.data} pending={incoming.isPending} />}

      {tab === 'work' && related && <CompanyWork related={related} company={c.displayName ?? c.legalName} who={who} viewer={viewer} />}

      {tab === 'files' && (
        <section className="flex flex-col gap-2" data-testid="drive-folders">
          <Kicker>In the shared drive</Kicker>
          {folders.data && folders.data.folders.length > 0 ? (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {folders.data.folders.map((f) => (
                <DriveFolder key={f.key} folder={f} companyId={f.forCompany ? c.id : undefined} who={who} viewer={viewer} />
              ))}
            </ul>
          ) : (
            <p className="m-0 text-dense text-secondary">
              {c.relationship === 'unapproved'
                ? 'Nothing yet: the taxonomy files papers by what a company is to us, and nobody has said.'
                : 'The taxonomy names no folder for this relationship.'}
            </p>
          )}
        </section>
      )}

      <AgentBrief
        lines={[
          ['company', c.id],
          ['domain', c.domain],
          ['relationship', c.relationship],
          ['people_now', c.peopleNow],
        ]}
      />
      {flow === 'edit' && <EditCompany company={c} onClose={() => setFlow(null)} />}
      {flow === 'details' && <WriteCompanyDetails companyId={c.id} current={details} onClose={() => setFlow(null)} />}
      {flow === 'communication' && <LogCommunication companyId={c.id} employments={c.employments} onClose={() => setFlow(null)} />}
      {flow === 'approve' && (
        <ApproveCounterparty kind="company" id={c.id} name={c.displayName ?? c.legalName} current={c.relationship} onClose={() => setFlow(null)} />
      )}
    </div>
  );
}

function ManualCommunications({ companyId }: { companyId: string }) {
  const query = useManualCommunications(companyId);
  const download = useDownloadManualCommunicationAttachment();
  const toast = useToast();
  const departmentLabel = useDepartmentLabel();
  if (query.isPending) return <Skeleton rows={3} />;
  if (query.isError) return <Refusal failure={failureOf(query.error)} />;
  if (!query.data.items.length)
    return <p className="m-0 text-dense text-secondary">No manual communication has been recorded.</p>;
  return (
    <ol className="m-0 flex list-none flex-col gap-3 p-0" aria-label="Manual communications">
      {query.data.items.map((item) => (
        <li key={item.id} className="rounded-brand border border-line p-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <Kicker>{humanise(item.kind)} · {humanise(item.direction)}</Kicker>
            <span className="text-label text-meta">{departmentLabel(item.department)} · {when(item.occurredAt)}</span>
          </div>
          <p className="mb-0 mt-2 whitespace-pre-wrap text-body">{item.summary}</p>
          {item.participants.length > 0 && <p className="mb-0 mt-2 text-dense text-secondary">With {item.participants.join(', ')}</p>}
          {item.outcome && <p className="mb-0 mt-2 text-dense"><strong>Outcome:</strong> {item.outcome}</p>}
          {item.nextAction && <p className="mb-0 mt-1 text-dense"><strong>Next action:</strong> {item.nextAction}</p>}
          {item.duplicateMessageId && <p className="mb-0 mt-2 text-dense text-warning">This provider event is already present in connected history; this remains a manual note, not a second message.</p>}
          {item.provider && <p className="mb-0 mt-1 text-label text-meta">Source reference: {humanise(item.provider)} · {item.providerEventId}</p>}
          {item.attachments.length > 0 && (
            <ul className="mb-0 mt-2 flex list-none flex-wrap gap-2 p-0" aria-label="Attached files">
              {item.attachments.map((attachment) => (
                <li key={attachment.id}><Button size="row" disabled={download.isPending} onClick={() => download.mutate(attachment.id, {
                  onSuccess: saveDownloadedFile,
                  onError: (error) => toast({ text: said(failureOf(error)), tone: 'danger' }),
                })}>{attachment.filename}</Button></li>
              ))}
            </ul>
          )}
          <p className="mb-0 mt-2 text-label text-meta">Recorded manually by {item.recordedByName}</p>
        </li>
      ))}
    </ol>
  );
}

function LogCommunication({ companyId, employments, onClose }: { companyId: string; employments: WireEmployment[]; onClose: () => void }) {
  const log = useLogManualCommunication();
  const upload = useUploadManualCommunicationAttachment();
  const toast = useToast();
  const [key] = useState(() => crypto.randomUUID());
  const localNow = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  const [form, setForm] = useState({ kind: 'call', direction: 'outbound', occurredAt: localNow,
    contactId: '', participants: '', summary: '', outcome: '', nextAction: '', provider: '', providerEventId: '' });
  const [createFollowUpTask, setCreateFollowUpTask] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      kicker="Communication"
      title="Log what happened"
      note="A manual record is distinct from connected-channel history and keeps your department and authorship."
      primary={{
        label: 'Save communication',
        disabled: !form.summary.trim() || !form.occurredAt || log.isPending || upload.isPending || Boolean(form.provider) !== Boolean(form.providerEventId.trim()),
        onClick: async () => {
          try {
            const saved = await log.mutateAsync({
          key, companyId,
          kind: form.kind as 'call' | 'meeting' | 'sms' | 'whatsapp' | 'linkedin' | 'email' | 'note',
          direction: form.direction as 'inbound' | 'outbound' | 'internal',
          occurredAt: new Date(form.occurredAt).toISOString(),
          contactId: form.contactId || null,
          participants: form.participants.split(',').map((part) => part.trim()).filter(Boolean),
          summary: form.summary, outcome: form.outcome || null, nextAction: form.nextAction || null,
          provider: form.provider ? form.provider as 'gmail' | 'google_chat' | 'whatsapp' | 'sms' | 'linkedin' | 'phone' : null,
          providerEventId: form.providerEventId || null,
          createFollowUpTask,
            });
            let remaining = [...files];
            for (const file of files) {
              await upload.mutateAsync({ companyId, manualCommunicationId: saved.record.id, filename: file.name,
                mediaType: file.type || 'application/octet-stream', dataBase64: await fileBase64(file) });
              remaining = remaining.slice(1);
              setFiles(remaining);
            }
            toast({ text: files.length ? 'Communication and files recorded.' : 'Communication recorded.' });
            onClose();
          } catch (error) {
            toast({ text: said(failureOf(error)), tone: 'danger' });
          }
        },
      }}
      secondary={{ label: 'Cancel', onClick: onClose }}
    >
      <div className="grid grid-cols-2 gap-3 phone:grid-cols-1">
        <Field label="Type"><Select aria-label="Type" value={form.kind} onChange={set('kind')} options={[
          'call','meeting','sms','whatsapp','linkedin','email','note'].map((value) => ({ value, label: humanise(value) }))} /></Field>
        <Field label="Direction"><Select aria-label="Direction" value={form.direction} onChange={set('direction')} options={[
          { value: 'inbound', label: 'Inbound' }, { value: 'outbound', label: 'Outbound' }, { value: 'internal', label: 'Internal note' }]}/></Field>
      </div>
      <Field label="When"><TextInput aria-label="When" type="datetime-local" value={form.occurredAt} onChange={set('occurredAt')} /></Field>
      <Field label="Contact (optional)"><Select aria-label="Contact" value={form.contactId} onChange={set('contactId')}
        options={[{ value: '', label: 'No contact selected' }, ...employments.map((employment) => ({
          value: employment.contactId, label: `${employment.contactName}${employment.current ? '' : ' · former'}` }))]} /></Field>
      <Field label="Participants (comma separated)"><TextInput aria-label="Participants" value={form.participants} onChange={set('participants')} /></Field>
      <Field label="Summary"><Textarea aria-label="Summary" value={form.summary} onChange={set('summary')} /></Field>
      <Field label="Outcome (optional)"><Textarea aria-label="Outcome" value={form.outcome} onChange={set('outcome')} /></Field>
      <Field label="Next action (optional)"><Textarea aria-label="Next action" value={form.nextAction} onChange={set('nextAction')} /></Field>
      <div className="grid grid-cols-2 gap-3 phone:grid-cols-1">
        <Field label="Connected provider (optional)"><Select aria-label="Connected provider" value={form.provider} onChange={set('provider')} options={[
          { value: '', label: 'No connected event' }, { value: 'gmail', label: 'Gmail' }, { value: 'google_chat', label: 'Google Chat' },
          { value: 'whatsapp', label: 'WhatsApp' }, { value: 'sms', label: 'SMS' }, { value: 'linkedin', label: 'LinkedIn' }, { value: 'phone', label: 'Phone' },
        ]} /></Field>
        <Field label="Provider event id (optional)"><TextInput aria-label="Provider event id" value={form.providerEventId} onChange={set('providerEventId')} /></Field>
      </div>
      <Field label="Files (optional, up to 5 MB each)">
        <input aria-label="Files" type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.csv,.txt,.docx,.xlsx,.pptx" onChange={(event) => setFiles(Array.from(event.target.files ?? []).slice(0, 5))} />
      </Field>
      {files.length > 0 && <p className="m-0 text-label text-meta">{files.map((file) => file.name).join(', ')}</p>}
      <label className="flex items-center gap-2 text-dense">
        <input type="checkbox" checked={createFollowUpTask} disabled={!form.nextAction.trim()}
          onChange={(event) => setCreateFollowUpTask(event.target.checked)} />
        Add the next action as my task below the calendar
      </label>
    </Dialog>
  );
}

function CommunicationCapabilities() {
  const query = useCommunicationCapabilities();
  if (query.isPending) return <Skeleton rows={2} />;
  if (query.isError) return <Refusal failure={failureOf(query.error)} compact />;
  return (
    <section className="flex flex-col gap-2" aria-label="Communication channels">
      <Kicker>Connected channels</Kicker>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {query.data.channels.map((channel, index) => (
          <li key={`${channel.channel}:${channel.account ?? index}`} className="rounded-brand border border-line px-3 py-2 text-dense">
            <strong>{humanise(channel.channel)}{channel.account ? ` · ${channel.account}` : ''}</strong>
            <span className="ml-2 text-meta">{humanise(channel.status)}</span>
            <div className="mt-1 text-label text-secondary">
              {channel.status === 'unavailable' ? channel.problem : [channel.capabilities.read && 'history', channel.capabilities.draft && 'native drafts', channel.capabilities.send && 'approved send'].filter(Boolean).join(' · ')}
              {channel.importGap && ` · import gap ${when(channel.importGap.from)} to ${when(channel.importGap.to)}`}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

async function fileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      if (comma < 0) reject(new Error('The file could not be encoded.'));
      else resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

function saveDownloadedFile(file: { filename: string; mediaType: string; dataBase64: string }) {
  const raw = atob(file.dataBase64);
  const bytes = Uint8Array.from(raw, (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: file.mediaType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function detailsSource(d: NonNullable<WireProspect['details']>): string {
  const on = d.at ? ` on ${when(d.at)}` : '';
  if (d.source === 'person') return `Written down by ${d.by ?? 'a colleague'}${on}`;
  if (d.source === 'own_site') return `From its own website${on}`;
  if (d.source === 'register') return `From its national register${on}`;
  return `From research${on}`;
}

/** What this department holds about the company: its prospects, buyers, supplier record and signals. */
function CompanyWork({ related, company, who, viewer }: { related: WireCompanyRelated; company: string; who: Who; viewer: Viewer }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-5" data-testid="company-work">
      <RelatedList
        title="As a prospect"
        rows={related.prospects.map((p) => ({
          id: p.id,
          title: p.name ?? 'Name not written down yet',
          sub: [stateWords(p.stage), p.marketCode, p.fitScore !== null ? `fit ${p.fitScore}` : null].filter(Boolean).join(' · '),
          onOpen: () => void navigate(`/prospects/${p.id}`),
          context: explainRelatedProspect(p, who, viewer, company),
        }))}
        empty="Not a prospect of this department."
      />
      <RelatedList
        title="As a buyer"
        rows={related.buyers.map((b) => ({ id: b.id, title: b.name, sub: b.marketCode, onOpen: () => void navigate(`/buyers/${b.id}`), context: explainRelatedBuyer(b, who, viewer, company) }))}
        empty="Not a buyer of this department."
      />
      <RelatedList
        title="As a supplier"
        rows={related.suppliers.map((x) => ({
          id: x.id,
          title: x.name,
          sub: `${x.country} · ${x.role === 'current' ? 'our supplier' : 'an alternative'}`,
          onOpen: () => void navigate(`/suppliers/${x.id}`),
          context: explainRelatedSupplier(x, who, viewer, company),
        }))}
        empty="Not a supplier of this department."
      />
      <RelatedList
        title="What was noticed"
        rows={related.signals.map((g) => ({ id: g.id, title: g.said, sub: [humanise(g.kind), g.seenOn, g.source].filter(Boolean).join(' · '), context: explainRelatedSignal(g, who, viewer, company) }))}
        empty="No signal noticed about it."
      />
    </div>
  );
}

function WriteCompanyDetails({ companyId, current, onClose }: { companyId: string; current: NonNullable<WireProspect['details']> | null; onClose: () => void }) {
  const write = useWriteCompanyRecord();
  const toast = useToast();
  const [form, setForm] = useState({
    postalAddress: current?.postalAddress ?? '',
    phone: current?.phone ?? '',
    website: current?.website ?? '',
    orgNumber: current?.orgNumber ?? '',
    note: current?.note ?? '',
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const filled = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim())) as Partial<typeof form>;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Company"
      title="Write the details down"
      note="What you read on its website, its register or its listing. Everyone who works with this company sees it."
      primary={{
        label: 'Save',
        disabled: Object.keys(filled).length === 0 || write.isPending,
        onClick: () =>
          write.mutate(
            { companyId, ...filled },
            { onSuccess: () => { toast({ text: 'Written down on the company.' }); onClose(); }, onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
          ),
      }}
      secondary={{ label: 'Cancel', onClick: onClose }}
    >
      <Field label="Address"><TextInput value={form.postalAddress} onChange={set('postalAddress')} aria-label="Address" /></Field>
      <Field label="Phone"><TextInput value={form.phone} onChange={set('phone')} aria-label="Phone" /></Field>
      <Field label="Website"><TextInput value={form.website} onChange={set('website')} aria-label="Website" /></Field>
      <Field label="Org number"><TextInput value={form.orgNumber} onChange={set('orgNumber')} aria-label="Org number" /></Field>
      <Field label="Note"><Textarea value={form.note} onChange={set('note')} aria-label="Note" /></Field>
    </Dialog>
  );
}

function employmentEntry(side: 'company' | 'contact', who: Who, viewer: Viewer) {
  return (e: WireEmployment) => ({
    id: e.id,
    title:
      side === 'company' ? (
        <Link to={`/companies/${e.companyId}`} className="text-ink">{e.companyName}</Link>
      ) : (
        <Link to={`/contacts/${e.contactId}`} className="text-ink">{e.contactName}</Link>
      ),
    body: e.roleTitle,
    context: explainEmployment(e, side, who, viewer),
    when: e.validTo ? `${e.validFrom} — ${e.validTo}` : `From ${e.validFrom} — current`,
    current: e.current,
    chip: e.current ? { label: 'CURRENT', tone: 'accent' as const } : { label: side === 'company' ? 'CLOSED' : 'MOVED ON', tone: 'neutral' as const },
  });
}

export function Incoming({ incoming, pending, companyId }: { incoming: IncomingResponse | undefined; pending: boolean; companyId?: string }) {
  const navigate = useNavigate();
  const who = useWho();
  const me = useMeOrThrow();
  const viewer = viewerOf(me);
  return (
    <section className="flex flex-col gap-3" aria-label="Conversations">
      <Kicker>Everything you have discussed</Kicker>
      {pending && <Skeleton rows={2} />}
      {incoming && incoming.threads.length === 0 && (
        <p className="m-0 text-dense text-secondary">No conversation is filed against this record in your department.</p>
      )}
      {incoming && incoming.threads.length > 0 && (
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {incoming.threads.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className="flex w-full flex-wrap items-baseline gap-2 rounded-brand border border-line px-4 py-3 text-left hover:bg-raised"
                onClick={() => void navigate(`/mail/${t.id}`)}
              >
                <span className="text-body font-semibold">{t.subject ?? '(no subject)'}</span>
                <span className="text-dense text-secondary">{t.lastFrom ? `last from ${t.lastFrom}` : ''}</span>
                <span className="ml-auto text-dense text-meta">{when(t.lastMessageAt)}</span>
                {statusChip(t.needsReply ? 'reply needed' : t.state) && (
                  <span className="text-label uppercase tracking-label text-meta">{t.needsReply ? 'reply needed' : stateWords(t.state).toLowerCase()}</span>
                )}
              </button>
              {companyId && <ConversationActions companyId={companyId} thread={t} />}
              <Explainer context={explainLinkedThread(t, who, viewer)} />
            </li>
          ))}
        </ol>
      )}
      {incoming && incoming.links.length > 0 && (
        <RelatedList
          title="Also linked"
          empty="Nothing else is linked here."
          rows={incoming.links.map((l) => ({
            id: l.id,
            title: linkedWords(l.sourceTable),
            sub: `${humanise(l.relation)} · linked by ${actorLabel(l.linkedBy)} · ${when(l.linkedAt)}`,
            context: explainLink(l, who, viewer),
          }))}
        />
      )}
    </section>
  );
}

function ConversationActions({ companyId, thread }: { companyId: string; thread: IncomingResponse['threads'][number] }) {
  const frame = useFrameMaybe();
  const create = useCreateCommunicationTask();
  const toast = useToast();
  const [reviewOn, setReviewOn] = useState('');
  const add = (objective: 'send_reply' | 'follow_up' | 'wait_reply', prepareDraft: boolean) => {
    const reviewAt = reviewOn ? new Date(`${reviewOn}T09:00:00`).toISOString() : null;
    create.mutate({ key: crypto.randomUUID(), companyId, threadId: thread.id, objective,
      title: objective === 'send_reply' ? `Send reply: ${thread.subject ?? 'conversation'}` : objective === 'wait_reply' ? `Review reply: ${thread.subject ?? 'conversation'}` : `Follow up: ${thread.subject ?? 'conversation'}`,
      reviewAt, prepareDraft }, {
      onSuccess: (result) => toast({ text: result.draft && !['queued','already_queued'].includes(result.draft.outcome)
        ? `Task added. Draft is waiting: ${stateWords(result.draft.outcome).toLowerCase()}.`
        : prepareDraft ? 'Reply task added; the twin draft is queued.' : 'Communication task added.' }),
      onError: (error) => toast({ text: said(failureOf(error)), tone: 'danger' }),
    });
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 pl-2">
      {frame && <Button size="row" onClick={() => frame.ask(
        'Analyze this conversation using only sources I can access. Cite the relevant messages and company records, identify missing information, and propose the next action. Do not send anything.',
        { page: 'thread', threadId: thread.id },
      )}>Analyze with my twin</Button>}
      <Button size="row" disabled={create.isPending} onClick={() => add('send_reply', true)}>Draft + send task</Button>
      <TextInput aria-label={`Review date for ${thread.subject ?? 'conversation'}`} type="date" value={reviewOn} onChange={(event) => setReviewOn(event.target.value)} />
      <Button size="row" disabled={create.isPending || !reviewOn} onClick={() => add('wait_reply', false)}>Wait for reply</Button>
      <Button size="row" disabled={create.isPending} onClick={() => add('follow_up', false)}>Follow up</Button>
    </div>
  );
}

/**
 * One folder of the shared drive, and — for the department the person is
 * working in — what is in it, read on a press as them under their own drive
 * consent (ADR-0090, ADR-0091, P:720-748). Another department's folder is
 * named and never opened from here.
 */
function DriveFolder({ folder: f, companyId, who, viewer }: { folder: WireFolder; companyId: string | undefined; who: Who; viewer: Viewer }) {
  const tech = useTechnical();
  const departmentLabel = useDepartmentLabel();
  const me = useMeOrThrow();
  const [open, setOpen] = useState(false);
  const files = useDriveFiles(f.key, companyId, open);
  const ours = f.department === me.department;
  return (
    <li className="flex flex-col gap-2 rounded-brand border border-line px-3 py-2 text-dense" data-testid="drive-folder">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="flex min-w-0 flex-col">
          <span className="font-medium [overflow-wrap:anywhere]">{f.path}</span>
          <span className="text-meta">
            {tech && <code className="font-mono text-dense">{f.key} · </code>}
            {f.forCompany ? 'its own folder, named for it · ' : ''}kept {f.retention === 'permanent' ? 'permanently' : f.retention} · written by{' '}
            {f.writableBy.map((d) => departmentLabel(d)).join(' and ')}
          </span>
        </span>
        {ours && (
          <Button size="row" variant="quiet" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? 'Hide' : 'What is in it'}
          </Button>
        )}
      </div>
      <Explainer context={explainFolder(f, who, viewer, f.forCompany === true)} />
      {open && (
        <div data-testid="drive-files">
          {files.isPending && <p className="m-0 text-meta">Reading the folder in Drive, as you…</p>}
          {files.isError && <Refusal compact failure={failureOf(files.error)} />}
          {files.data && files.data.files.length === 0 && <p className="m-0 text-meta">Nothing in it yet.</p>}
          {files.data && files.data.files.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0" aria-label={`In ${f.path}`}>
              {files.data.files.map((file) => (
                <li key={`${file.name}-${file.modifiedTime ?? ''}`} className="flex flex-wrap items-center justify-between gap-2 bg-page px-3 py-2">
                  <span className="flex min-w-0 flex-col">
                    {file.link ? (
                      <a href={file.link} target="_blank" rel="noreferrer noopener" className="text-ink [overflow-wrap:anywhere]">
                        {file.isFolder ? `${file.name}/` : file.name}
                      </a>
                    ) : (
                      <span className="[overflow-wrap:anywhere]">{file.isFolder ? `${file.name}/` : file.name}</span>
                    )}
                    <span className="text-dense text-meta">
                      {file.modifiedTime ? `changed ${when(file.modifiedTime)}` : 'never changed'}
                      {file.lastModifiedBy ? ` by ${file.lastModifiedBy}` : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Hand a contact to a colleague (ADR-0118): shown to whoever holds them and
 * to a superadmin, which is who the platform lets do it. The colleague is
 * named by their address; the platform finds them or says it cannot.
 */
function HandOver({ contactId, ownerHuman }: { contactId: string; ownerHuman: string | null }) {
  const me = useMeOrThrow();
  const assign = useAssignContact();
  const toast = useToast();
  const [to, setTo] = useState('');
  if (ownerHuman !== me.humanId && !me.isSuperadmin) return null;
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      data-testid="hand-over"
      onSubmit={(e) => {
        e.preventDefault();
        assign.mutate(
          { contactId, toEmail: to },
          {
            onSuccess: (r) => {
              toast({ text: `Handed to ${r.to}. The receipt names you both.` });
              setTo('');
            },
            onError: (err) => toast({ text: said(failureOf(err)), tone: 'danger' }),
          },
        );
      }}
    >
      <label className="text-dense text-meta" htmlFor="hand-to">Hand to</label>
      <input
        id="hand-to"
        type="email"
        className="min-h-tap min-w-[220px] border border-control bg-raised px-3 text-dense"
        placeholder="a colleague's address"
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <Button size="row" type="submit" disabled={!to.trim() || assign.isPending}>Hand over</Button>
    </form>
  );
}

// What links here, by what it is: the table a link lives in is the
// platform's name for it, never a heading.
const LINKED: Record<string, string> = { thread: 'A conversation', message: 'An email', deal: 'A deal', order: 'An order', sample: 'A sample', invoice: 'An invoice', task: 'A task', project: 'A project' };
function linkedWords(table: string): string {
  return LINKED[table] ?? `A ${humanise(table).toLowerCase()}`;
}
